import Layout from '@/components/template/Layout';
import { lessonsQueryKeys } from '@/data/services/querykeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingView from '../courses/LoadingView';
import LessonBoxForm from '@/components/_deprecated/courses/box/lesson/LessonBoxForm';
import ErrorPage from '../notFound/ErrorPage';
import Lesson from '@/models/Lesson';
import { buildChangedObject } from '@/utils/buildChangedObject';
import {
  LessonDTO,
  updateLesson,
  updateLessonInBank,
} from '@/data/services/lessonServices';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import { UsersIcon } from '@heroicons/react/24/outline';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { ArrowUpTrayIcon } from '@/components/icons';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import ModalWarning from '@/components/ui/custom/modals/ModalWarning';
import useToggle from '@/data/hook/useToggle';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { ReactNode, useEffect, useRef } from 'react';
import { ROUTES } from '@/utils/routes';

export default function DeatchedLessonEditPage() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'detachedLessonEditPage',
  });
  const lessonDefaultValuesRef = useRef<Lesson>();
  const { lessonId } = useParams();
  const { queryKey, queryFn } = lessonsQueryKeys.get(Number(lessonId));
  const { data: lesson, isInitialLoading: isLoading } = useQuery({
    queryKey,
    queryFn,
    enabled: !!lessonId,
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    isOpen: isPublishModalOpen,
    open: openPublishModal,
    close: closePublishModal,
  } = useToggle();
  const invalidateLessons = async () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: lessonsQueryKeys.list._def });
  };
  async function updateLessonMutation(params: {
    changes: Partial<LessonDTO>;
    id: number;
  }) {
    const { changes, id } = params;
    if ('inBank' in changes || 'isPublicInBank' in changes) {
      await updateLessonInBank({
        lessonId: id,
        bank: {
          inBank: changes.inBank ?? undefined,
          isPublicInBank: changes.isPublicInBank ?? undefined,
        },
      });
    }
    return updateLesson({ lessonId: id }, changes);
  }
  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: updateLessonMutation,
    onSuccess: invalidateLessons,
  });

  async function publishLesson(allowedUnitsIds?: number[]) {
    if (!lesson) return;
    return await updateLesson(
      { lessonId: lesson.id },
      { allowedUnitsIds, status: VersioningStatusEnum.PUBLISHED },
    );
  }

  async function publishForGeneralUse() {
    return await publishLesson([]);
  }

  async function onPublish() {
    await invalidateLessons();
    closePublishModal();
    navigate(ROUTES.COURSES.BASE);
  }

  const {
    mutate: onPublishForGeneralUseMutation,
    isPending: isPublishingForGeneralUse,
  } = useMutation({
    mutationFn: publishForGeneralUse,
    onSuccess: onPublish,
  });

  function handleConfirmPublish() {
    onPublishForGeneralUseMutation();
  }

  async function publishForUnits() {
    return await publishLesson();
  }

  const { mutate: onPublishForUnitsMutation, isPending: isPublishingForUnits } =
    useMutation({
      mutationFn: publishForUnits,
      onSuccess: onPublish,
    });

  const onSubmit: (params: {
    lesson: Partial<Lesson>;
    validated: boolean;
  }) => void = async params => {
    if (!lesson) return;
    const { lesson: lessonChanges } = params;
    const changes = buildChangedObject<Partial<LessonDTO>>(
      { ...lesson, tools: lesson.tools?.map(tool => tool.id) },
      { ...lessonChanges, tools: lessonChanges.tools?.map(tool => tool.id) },
    );

    update({ id: lesson.id, changes });
  };

  const lessonFormActions = (
    <div className="flex gap-3 items-center">
      <ConditionalRenderer condition={lesson?.allowedUnitsIds?.length}>
        <PublishButton
          text={t('publishForSelectedUnits')}
          icon={<UsersIcon />}
          onClick={() => onPublishForUnitsMutation()}
          isLoading={isPublishingForUnits}
        />
      </ConditionalRenderer>
      <PublishButton
        text={t('publishForGeneralUse')}
        icon={<ArrowUpTrayIcon className="w-4 h-4" />}
        onClick={openPublishModal}
        isLoading={isPublishingForGeneralUse}
      />
    </div>
  );

  useEffect(() => {
    if (!!lessonDefaultValuesRef.current || !lesson) return;

    lessonDefaultValuesRef.current = { ...lesson };
  }, [lesson]);

  const defaultValues = lessonDefaultValuesRef.current;

  const hasChanges =
    defaultValues && JSON.stringify(lesson) !== JSON.stringify(defaultValues);

  if (isLoading) {
    return <LoadingView />;
  }

  if (!lesson) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <Layout>
      <LessonBoxForm
        updateLessons={invalidateLessons}
        lesson={lesson}
        onSubmit={debounce(onSubmit, 500)}
        isLoading={isUpdating}
        className={{
          tabsContainer: 'flex-1',
          tabsContent: 'flex-1',
        }}
        actions={lessonFormActions}
        backUrl={ROUTES.COURSES.BASE}
        hasChanges={hasChanges}
      />
      <ModalWarning
        visible={isPublishModalOpen}
        translationString="modalPublishLesson"
        onClickConfirm={handleConfirmPublish}
        onClickCancel={closePublishModal}
        isLoading={isPublishingForGeneralUse}
      />
    </Layout>
  );
}

type PublishButtonsProps = {
  text: string;
  icon: ReactNode;
  onClick(): void | Promise<void>;
  isLoading: boolean;
};

function PublishButton({
  icon,
  onClick,
  text,
  isLoading,
}: PublishButtonsProps) {
  return (
    <MainButton
      text={text}
      icon={icon}
      onClick={onClick}
      loading={isLoading}
      color="custom"
      className="flex-row-reverse font-600 text-primary gap-1"
    />
  );
}
